import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { type AutocompleteInputChangeReason, inputAdornmentClasses } from '@mui/material';
import { ComboBox, generateUtilityClasses } from '@kc-console/common-ui';
import { getSearchTextMatchPriority } from '@kc-console/core';

import { EllipsisText } from '../EllipsisText';

import type { InlinePropertyFilterProps } from './types';

type FilterStep = 'key' | 'value';

export default function InlinePropertyFilter(props: InlinePropertyFilterProps) {
  const {
    autoFocus = true,
    className,
    filterKey: filterKeyProp,
    filterKeyDefaultOption: _filterKeyDefaultOption,
    filterKeyOptions,
    placeholder = 'Enter property and value...',
    filterValue: filterValueProp,
    filterValueOptions = [],
    isFilterValueOptionsLoading,
    onFilterKeyChange,
    onFilterValueChange,
    onSubmit,
    disabled = false,
    ...others
  } = props;

  const [currentStep, setCurrentStep] = useState<FilterStep>('key');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [selectedKeyText, setSelectedKeyText] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // 현재 스텝에 따른 옵션 결정
  const currentOptions =
    currentStep === 'key'
      ? filterKeyOptions.map(option => option.text ?? option.value)
      : (filterValueOptions?.map(option => option.text ?? '') ?? []);

  // input에 표시될 값 (value 단계에서는 순수한 값만)
  const displayValue = inputValue;

  const handleInputChange = useCallback(
    (event: unknown, value: string, reason: AutocompleteInputChangeReason) => {
      if (currentStep === 'key') {
        setInputValue(value);

        // 키 입력 중
        const trimmedValue = value.trim();

        // 옵션을 선택했거나 콜론이 포함된 경우 값 단계로 전환
        if (reason === 'selectOption' || trimmedValue.includes(':')) {
          let keyToFind = trimmedValue;
          let remainingValue = '';

          if (trimmedValue.includes(':')) {
            // 콜론이 포함된 경우 키와 값 분리
            const [keyPart, ...valueParts] = trimmedValue.split(':');
            keyToFind = keyPart;
            remainingValue = valueParts.join(':');
          }

          const foundKey = filterKeyOptions.find(
            option =>
              option.value.toLowerCase() === keyToFind.toLowerCase() ||
              (option.text ?? option.value).toLowerCase() === keyToFind.toLowerCase(),
          );

          if (foundKey) {
            setSelectedKey(foundKey.value);
            setSelectedKeyText(foundKey.text ?? foundKey.value);
            setCurrentStep('value');
            setInputValue(remainingValue);
            onFilterKeyChange?.(foundKey.value);
            // value 단계에서 옵션이 있으면 드롭다운 열기
            setTimeout(() => setIsOpen(true));
          }
        }
      } else if (currentStep === 'value' && selectedKeyText) {
        if (reason === 'selectOption') {
          const foundOption = filterValueOptions.find(option => option.text === value);
          if (foundOption) {
            handleSubmit(value);
            return;
          }
        }
        // value 단계에서는 순수한 값만 처리
        setInputValue(value);
        onFilterValueChange?.(value);
      }
    },
    [
      currentStep,
      filterKeyOptions,
      selectedKeyText,
      filterValueOptions.length,
      onFilterKeyChange,
      onFilterValueChange,
    ],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      if (currentStep === 'key' && inputValue.trim()) {
        // 키 단계에서 Enter를 누르면 해당 키로 값 단계로 전환
        const trimmedValue = inputValue.trim();
        const foundKey = filterKeyOptions.find(
          option =>
            option.value.toLowerCase() === trimmedValue.toLowerCase() ||
            (option.text ?? option.value).toLowerCase() === trimmedValue.toLowerCase(),
        );

        if (foundKey) {
          setSelectedKey(foundKey.value);
          setSelectedKeyText(foundKey.text ?? foundKey.value);
          setCurrentStep('value');
          setInputValue('');
          onFilterKeyChange?.(foundKey.value);
          // value 단계에서 옵션이 있으면 드롭다운 열기
          setTimeout(() => setIsOpen(true));
          event.preventDefault(); // 기본 Enter 동작 방지
        }
      } else if (currentStep === 'value' && selectedKey && inputValue.trim()) {
        handleSubmit(inputValue);
      }
    } else if (event.key === 'Backspace') {
      if (currentStep === 'value' && inputValue === '' && selectedKey) {
        // value 스텝에서 입력이 없을 때 Backspace로 key 스텝으로 돌아가기
        setCurrentStep('key');
        setSelectedKey(null);
        setSelectedKeyText(null);
        setInputValue('');
        onFilterKeyChange?.('');
      }
    }
  };

  const handleSubmit = (value: string) => {
    if (selectedKey && value.trim()) {
      onSubmit?.({
        filterKey: selectedKey,
        filterValue: value,
      });
      // 초기화
      resetToKeyStep();
    }
  };

  const resetToKeyStep = useCallback(() => {
    setCurrentStep('key');
    setSelectedKey(null);
    setSelectedKeyText(null);
    setInputValue('');
    onFilterKeyChange?.('');
    onFilterValueChange?.('');
    // 포커스 유지
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [onFilterKeyChange, onFilterValueChange]);

  // filterKeyProp이 변경되면 내부 상태 동기화
  useEffect(() => {
    if (filterKeyProp !== undefined && filterKeyProp !== selectedKey) {
      if (filterKeyProp) {
        const foundKey = filterKeyOptions.find(option => option.value === filterKeyProp);
        if (foundKey) {
          setSelectedKey(foundKey.value);
          setSelectedKeyText(foundKey.text ?? foundKey.value);
          setCurrentStep('value');
        }
      } else {
        // filterKeyProp이 빈 값이면 완전 초기화
        resetToKeyStep();
      }
    }
  }, [filterKeyProp, selectedKey, filterKeyOptions, resetToKeyStep]);

  // filterValueProp이 변경되면 inputValue 동기화
  useEffect(() => {
    if (filterValueProp !== undefined) {
      if (currentStep === 'value') {
        setInputValue(filterValueProp);
      }
    } else {
      // filterValueProp이 undefined이면 input 초기화
      setInputValue('');
    }
  }, [filterValueProp, currentStep]);

  // 컴포넌트 마운트 시 autoFocus 처리
  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus, currentStep]);

  // key 스텝에서 필터링된 옵션들
  const filteredKeyOptions =
    currentStep === 'key'
      ? filterKeyOptions
          .filter(option =>
            Number.isFinite(
              getSearchTextMatchPriority(String(option.text ?? option.value), x => x, inputValue),
            ),
          )
          .map(option => option.text ?? option.value)
      : currentOptions;

  // value 단계에서 옵션이 있을 때 드롭다운을 열어야 하는지 결정
  const shouldShowDropdown =
    (currentStep === 'key' && filteredKeyOptions.length > 0) ||
    (currentStep === 'value' && currentOptions.length > 0);

  return (
    <InlinePropertyFilterRoot
      className={clsx(inlinePropertyFilterClasses.root, className)}
      {...others}
    >
      <InlinePropertyFilterInput
        className={inlinePropertyFilterClasses.input}
        disabled={disabled}
        options={currentStep === 'key' ? filteredKeyOptions : currentOptions}
        value={displayValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        open={isOpen && shouldShowDropdown}
        size="md"
        placeholder={placeholder}
        loading={isFilterValueOptionsLoading}
        inputRef={inputRef}
        startAdornment={
          currentStep === 'value' && selectedKeyText ? (
            <KeyPrefix>
              <EllipsisText className={'ellipsis'} variant="inherit">
                {selectedKeyText}
              </EllipsisText>
              :
            </KeyPrefix>
          ) : undefined
        }
        getOptionDisabled={option =>
          (currentStep === 'key' ? filterKeyOptions : filterValueOptions)?.some(
            o => o.text === option && o.disabled,
          ) ?? false
        }
      />
    </InlinePropertyFilterRoot>
  );
}

const inlinePropertyFilterClasses = generateUtilityClasses('InlinePropertyFilter', [
  'root',
  'input',
]);

const InlinePropertyFilterRoot = styled('div')`
  position: relative;
  width: 300px;
`;

const InlinePropertyFilterInput = styled(ComboBox)(
  () => css`
    width: 100%;
    .${inputAdornmentClasses.positionStart} {
      margin-right: 0px;
    }
  `,
);

const KeyPrefix = styled('span')(
  ({ theme }) => css`
    ${theme.typography['body/normal/sm']};
    display: inline-flex;
    align-items: center;
    max-width: 140px;
    color: ${theme.color['text/default']};

    & > .ellipsis {
      flex: 1 1;
      min-width: 0;
      display: inline-block;
    }
  `,
);
